import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import {
  ScanFace, MapPin, MonitorSmartphone, CalendarCheck, Wrench, BarChart3,
  Star, ShieldCheck, Smartphone, Send, ArrowRight, Building2, Users, Zap,
  LogIn, CheckCircle2,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" } }),
};

const FEATURES = [
  { icon: ScanFace, color: "#22D3A5", title: "Face Biometric Check-In", desc: "AI face recognition with 3-sample enrollment. No cards, no proxies — your face is your timesheet." },
  { icon: MapPin, color: "#4F8EF7", title: "GPS Geo-Tagging", desc: "Every punch records exact location & accuracy. Know who checked in and where." },
  { icon: MonitorSmartphone, color: "#9B6FEB", title: "Kiosk Mode", desc: "One device at the door scans the whole team — separate Check-In and Check-Out modes." },
  { icon: CalendarCheck, color: "#F7C94F", title: "Leave Manager", desc: "Casual, sick, earned & comp leaves with one-tap approvals and auto stat tracking." },
  { icon: Wrench, color: "#F75A5A", title: "Regularization", desc: "Missed a punch? Request a fix — approvals patch the attendance record automatically." },
  { icon: BarChart3, color: "#22D3A5", title: "Reports & CSV Export", desc: "Monthly attendance %, per-employee breakdowns and one-tap CSV downloads." },
  { icon: Star, color: "#9B6FEB", title: "Holiday Calendar", desc: "National, regional & company holidays — auto-excluded from working day counts." },
  { icon: Send, color: "#4F8EF7", title: "Daily Backup Reports", desc: "Send daily attendance summaries via Email or WhatsApp in one tap." },
];

const USPS = [
  { title: "Zero hardware needed", desc: "No biometric machines, no cards. Works on any phone browser — install it like an app." },
  { title: "Multi-company SaaS", desc: "Register your company in 60 seconds. Every workspace is fully isolated and private." },
  { title: "Anti-proxy by design", desc: "Strict best-match AI + GPS proof on every punch. Buddy-punching becomes impossible." },
  { title: "Built mobile-first", desc: "Designed for 375px screens up. Bottom-sheet flows, big touch targets, dark control-room UI." },
];

const STEPS = [
  { icon: Building2, title: "Register your company", desc: "Company name, your mobile & a PIN. You're the admin — done in a minute." },
  { icon: Users, title: "Add team & enroll faces", desc: "Add employees by mobile number. Enroll faces with a quick 3-angle scan." },
  { icon: Zap, title: "Scan in daily", desc: "Self check-in with face + GPS, or run Kiosk mode on one device at the door." },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  return (
    <div className="mx-auto w-full max-w-md min-h-[100svh] relative" style={{ background: "#0A0D14" }} data-testid="landing-page">
      {/* ambient glows */}
      <div className="absolute -top-32 -right-24 w-80 h-80 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(79,142,247,0.30), transparent 70%)" }} />
      <div className="absolute top-[40%] -left-32 w-80 h-80 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(34,211,165,0.18), transparent 70%)" }} />
      <div className="absolute inset-0 opacity-20 geo-grid pointer-events-none" />

      {/* Top bar */}
      <div className="sticky top-0 z-40" style={{ background: "#0A0D14", borderBottom: "1px solid #1E2640" }}>
        <div className="h-safe-top" />
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-syne font-bold" style={{ background: "#4F8EF7", color: "#fff" }}>S</div>
            <span className="font-syne font-bold tracking-tight" style={{ color: "#E8ECFF" }}>Swaagat</span>
          </div>
          <button
            onClick={() => nav(user ? "/" : "/login?mode=login")}
            data-testid="landing-top-login"
            className="h-9 px-4 rounded-lg text-xs font-bold flex items-center gap-1.5"
            style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#E8ECFF" }}
          >
            <LogIn size={13} /> {user ? "Dashboard" : "Login"}
          </button>
        </div>
      </div>

      <div className="relative px-5 pt-10 pb-16">
        {/* HERO */}
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] mb-5"
            style={{ background: "rgba(34,211,165,0.1)", border: "1px solid rgba(34,211,165,0.3)", color: "#22D3A5" }}>
            <ScanFace size={11} /> Face · GPS · Attendance
          </div>
        </motion.div>
        <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
          className="font-syne text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-4" style={{ color: "#E8ECFF" }}>
          Your face is<br />your timesheet.
        </motion.h1>
        <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2} className="text-sm leading-relaxed mb-8" style={{ color: "#7A85A3" }}>
          Swaagat is a mobile-first attendance system with AI face verification and GPS proof on every check-in. No hardware. No paperwork. No proxies.
        </motion.p>

        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="space-y-3 mb-10">
          <button
            onClick={() => nav("/login?mode=new")}
            data-testid="landing-cta-new-company"
            className="w-full h-13 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
            style={{ background: "#22D3A5", color: "#0A0D14" }}
          >
            <Building2 size={18} /> Register Your Company <ArrowRight size={16} />
          </button>
          <button
            onClick={() => nav("/login?mode=login")}
            data-testid="landing-cta-existing-login"
            className="w-full h-13 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
            style={{ background: "transparent", border: "1.5px solid #1E2640", color: "#E8ECFF" }}
          >
            <LogIn size={18} /> Existing Company Login
          </button>
        </motion.div>

        {/* Animated scan visual */}
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4}
          className="relative rounded-3xl overflow-hidden mb-12 p-6" style={{ background: "#111520", border: "1px solid #1E2640" }}>
          <div className="absolute inset-0 geo-grid opacity-30" />
          <div className="relative flex items-center justify-center py-6">
            <div className="relative" style={{ width: 150, height: 180 }}>
              {[
                "top-0 left-0 border-t-4 border-l-4 rounded-tl-2xl",
                "top-0 right-0 border-t-4 border-r-4 rounded-tr-2xl",
                "bottom-0 left-0 border-b-4 border-l-4 rounded-bl-2xl",
                "bottom-0 right-0 border-b-4 border-r-4 rounded-br-2xl",
              ].map((cls, i) => (
                <div key={i} className={`absolute w-9 h-9 animate-bracket ${cls}`} style={{ borderColor: "#4F8EF7" }} />
              ))}
              <div className="absolute inset-x-2 top-0 h-full overflow-hidden">
                <div className="absolute left-0 right-0 h-1 animate-scanline" style={{ background: "linear-gradient(90deg, transparent, #22D3A5, transparent)", boxShadow: "0 0 12px #22D3A5" }} />
              </div>
              <ScanFace size={56} className="absolute inset-0 m-auto" style={{ color: "#7A85A3" }} />
            </div>
          </div>
          <div className="relative grid grid-cols-3 gap-2 mt-4">
            {[
              { n: "<1s", l: "Face match" },
              { n: "100%", l: "Geo verified" },
              { n: "0", l: "Hardware" },
            ].map((s) => (
              <div key={s.l} className="rounded-xl p-3 text-center" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
                <div className="font-syne font-bold text-xl tracking-tighter" style={{ color: "#22D3A5" }}>{s.n}</div>
                <div className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: "#7A85A3" }}>{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* FEATURES */}
        <SectionLabel>Everything included</SectionLabel>
        <h2 className="font-syne text-2xl font-bold tracking-tight mb-6" style={{ color: "#E8ECFF" }}>
          One app. Full attendance stack.
        </h2>
        <div className="space-y-3 mb-14" data-testid="landing-features">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i % 4}
              className="rounded-2xl p-4 flex gap-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${f.color}1A`, border: `1px solid ${f.color}4D` }}>
                <f.icon size={19} style={{ color: f.color }} />
              </div>
              <div>
                <div className="font-bold text-sm mb-0.5" style={{ color: "#E8ECFF" }}>{f.title}</div>
                <div className="text-xs leading-relaxed" style={{ color: "#7A85A3" }}>{f.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* USP */}
        <SectionLabel>Why Swaagat</SectionLabel>
        <h2 className="font-syne text-2xl font-bold tracking-tight mb-6" style={{ color: "#E8ECFF" }}>
          Built different, on purpose.
        </h2>
        <div className="space-y-4 mb-14" data-testid="landing-usps">
          {USPS.map((u, i) => (
            <motion.div key={u.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i}
              className="flex gap-4">
              <div className="font-syne font-bold text-2xl tracking-tighter shrink-0 w-10" style={{ color: "#4F8EF7" }}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="pb-4" style={{ borderBottom: "1px solid #1E2640" }}>
                <div className="font-bold text-sm mb-1" style={{ color: "#E8ECFF" }}>{u.title}</div>
                <div className="text-xs leading-relaxed" style={{ color: "#7A85A3" }}>{u.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Security strip */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="rounded-2xl p-4 mb-14 flex items-center gap-3" style={{ background: "rgba(155,111,235,0.08)", border: "1px solid rgba(155,111,235,0.3)" }}>
          <ShieldCheck size={22} style={{ color: "#9B6FEB" }} className="shrink-0" />
          <div className="text-xs leading-relaxed" style={{ color: "#E8ECFF" }}>
            <span className="font-bold">Private by design.</span>{" "}
            <span style={{ color: "#7A85A3" }}>Face data stays as encrypted math vectors — never photos. Each company's workspace is fully isolated.</span>
          </div>
        </motion.div>

        {/* HOW IT WORKS */}
        <SectionLabel>How it works</SectionLabel>
        <h2 className="font-syne text-2xl font-bold tracking-tight mb-6" style={{ color: "#E8ECFF" }}>
          Live in 3 steps.
        </h2>
        <div className="space-y-3 mb-14" data-testid="landing-steps">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i}
              className="rounded-2xl p-4 flex gap-3 items-start" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-syne font-bold" style={{ background: "#1E2640", color: "#4F8EF7" }}>
                {i + 1}
              </div>
              <div>
                <div className="font-bold text-sm mb-0.5 flex items-center gap-1.5" style={{ color: "#E8ECFF" }}>
                  <s.icon size={14} style={{ color: "#22D3A5" }} /> {s.title}
                </div>
                <div className="text-xs leading-relaxed" style={{ color: "#7A85A3" }}>{s.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* PWA strip */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="rounded-2xl p-4 mb-14 flex items-center gap-3" style={{ background: "rgba(79,142,247,0.08)", border: "1px solid rgba(79,142,247,0.3)" }}>
          <Smartphone size={22} style={{ color: "#4F8EF7" }} className="shrink-0" />
          <div className="text-xs leading-relaxed" style={{ color: "#E8ECFF" }}>
            <span className="font-bold">Installs like a native app.</span>{" "}
            <span style={{ color: "#7A85A3" }}>Add to Home Screen on Android & iPhone — full-screen, fast, always a tap away.</span>
          </div>
        </motion.div>

        {/* FINAL CTA */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="rounded-3xl p-6 text-center" style={{ background: "#111520", border: "1px solid #1E2640" }}>
          <CheckCircle2 size={28} className="mx-auto mb-3" style={{ color: "#22D3A5" }} />
          <h2 className="font-syne text-2xl font-bold tracking-tight mb-2" style={{ color: "#E8ECFF" }}>
            Ready to go paperless?
          </h2>
          <p className="text-xs mb-5" style={{ color: "#7A85A3" }}>
            Set up your company workspace in under a minute.
          </p>
          <button
            onClick={() => nav("/login?mode=new")}
            data-testid="landing-bottom-cta"
            className="w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
            style={{ background: "#4F8EF7", color: "#fff" }}
          >
            Get Started Free <ArrowRight size={16} />
          </button>
        </motion.div>

        <div className="text-center text-[10px] uppercase tracking-widest mt-10 pb-safe" style={{ color: "#7A85A3" }}>
          Swaagat · Face + Geo Attendance · Made for mobile
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2" style={{ color: "#22D3A5" }}>
      {children}
    </div>
  );
}
